import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiX,
  FiEdit2,
  FiTrash2,
  FiDownload,
  FiChevronDown,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiImage,
  FiAlertTriangle,
  FiRefreshCw,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import NoData from "../components/NoData";
import Pagination from "../components/Pagination";
import ResponsiveModal from "../components/ResponsiveModal";
import { useAuth } from "../contexts/AuthContext";
import { formatDate, statusLabel, statusTone } from "../utils/format";
import { downloadSpreadsheet } from "../utils/download";

// Abas da fila de revisão. `status` vazio = todos.
const STATUS_TABS = [
  { status: "", label: "Todos" },
  { status: "PENDENTE", label: "Aguardando" },
  { status: "APROVADO", label: "Aprovados" },
  { status: "REJEITADO", label: "Reprovados" },
];

// Transições que o time pode aplicar. A opção do status atual é escondida.
const REVIEW_OPTIONS = [
  { status: "APROVADO", label: "Aprovar", color: "#166534", icon: FiCheckCircle },
  { status: "REJEITADO", label: "Reprovar", color: "#b91c1c", icon: FiXCircle },
  { status: "PENDENTE", label: "Voltar para análise", color: "#92400e", icon: FiClock },
];

const ITEMS_PER_PAGE = 20;

const statusText = (status) =>
  status === "REJEITADO" ? "Reprovado" : statusLabel(status);

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

// "há 3 dias" ao lado da data exata: o revisor precisa saber o que está parado.
const relativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours}h`;

  const days = Math.round(hours / 24);
  if (days < 30) return `há ${days} ${days === 1 ? "dia" : "dias"}`;

  const months = Math.round(days / 30);
  if (months < 12) return `há ${months} ${months === 1 ? "mês" : "meses"}`;

  const years = Math.round(months / 12);
  return `há ${years} ${years === 1 ? "ano" : "anos"}`;
};

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({ PENDENTE: 0, APROVADO: 0, REJEITADO: 0 });

  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("TODOS");
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [statusSavingId, setStatusSavingId] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);

  const [rejectingProduct, setRejectingProduct] = useState(null);
  const [rejectingBulk, setRejectingBulk] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  const [activeProductId, setActiveProductId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const statusMenuRef = useRef(null);

  const isStaff = ["ADMIN", "POWER_USER"].includes(user?.role);

  const fetchProducts = useCallback(
    async (page = 1) => {
      setLoadingProducts(true);
      try {
        const response = await axiosInstance.get("/products", {
          params: {
            itemsPerPage: ITEMS_PER_PAGE,
            page,
            ...(statusFilter ? { statusAprovacao: statusFilter } : {}),
          },
        });
        setProducts(response.data?.data?.data || []);
        setTotalPages(response.data?.data?.totalPages || 1);
        setTotalCount(response.data?.data?.totalCount || 0);
      } catch (error) {
        setProducts([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoadingProducts(false);
      }
    },
    [statusFilter]
  );

  // Os contadores das abas vêm de chamadas enxutas (1 item por página): só o
  // totalCount interessa, e assim a fila mostra o volume real de cada status.
  const fetchCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        ["PENDENTE", "APROVADO", "REJEITADO"].map((status) =>
          axiosInstance
            .get("/products", {
              params: { itemsPerPage: 1, page: 1, statusAprovacao: status },
            })
            .then((response) => response.data?.data?.totalCount || 0)
            .catch(() => 0)
        )
      );
      setCounts({ PENDENTE: results[0], APROVADO: results[1], REJEITADO: results[2] });
    } catch (error) {
      setCounts({ PENDENTE: 0, APROVADO: 0, REJEITADO: 0 });
    }
  }, []);

  useEffect(() => {
    if (!isStaff) return;
    fetchProducts(currentPage);
  }, [isStaff, currentPage, fetchProducts]);

  useEffect(() => {
    if (!isStaff) return;
    fetchCounts();
  }, [isStaff, fetchCounts]);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await axiosInstance.get("/categories", {
          params: { itemsPerPage: -1, isActive: true },
        });
        const allCategories =
          response.data?.data?.data || response.data?.data || [];
        const parents = allCategories.filter((cat) => !cat.parentId);
        setCategoriesTree(
          parents.map((parent) => ({
            ...parent,
            subcategories: allCategories.filter(
              (cat) => cat.parentId === parent.id
            ),
          }))
        );
      } catch (err) {
        setCategoriesTree([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!openStatusMenuId) return undefined;

    const handleClickOutside = (event) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setOpenStatusMenuId(null);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpenStatusMenuId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openStatusMenuId]);

  const resolveMediaSrc = (mediaItem) => {
    if (!mediaItem || typeof mediaItem !== "string") return "";
    if (
      mediaItem.startsWith("data:") ||
      mediaItem.startsWith("blob:") ||
      mediaItem.startsWith("http://") ||
      mediaItem.startsWith("https://")
    ) {
      return mediaItem;
    }
    return "";
  };

  const getProductImages = (product) => {
    const urls = Array.isArray(product?.mediaUrls) ? product.mediaUrls : [];
    const raw = Array.isArray(product?.media) ? product.media : [];
    const length = Math.max(urls.length, raw.length);

    return Array.from({ length }, (_, index) => urls[index] || resolveMediaSrc(raw[index]))
      .filter(Boolean);
  };

  const categoryNameOf = (product) => {
    const direct =
      product?.Category?.nome ||
      product?.Category?.name ||
      product?.category?.nome ||
      product?.category?.name ||
      product?.categoryName;
    if (direct) return direct;

    const categoryId = product?.Category?.id || product?.categoryId;
    if (!categoryId) return "";

    for (const parent of categoriesTree) {
      if (String(parent.id) === String(categoryId)) return parent.nome || parent.name;
      const sub = (parent.subcategories || []).find(
        (item) => String(item.id) === String(categoryId)
      );
      if (sub) return sub.nome || sub.name;
    }
    return "";
  };

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return products.filter((product) => {
      const title = product?.title || product?.name || "";
      const categoryName = categoryNameOf(product);
      const advertiser = product?.AdvertiserCompany || product?.Company;
      const advertiserName =
        advertiser?.nomeFantasia || advertiser?.razaoSocial || "";
      const categoryId =
        product?.Category?.id || product?.category?.id || product?.categoryId || "";
      const parentId =
        product?.Category?.parentId || product?.category?.parentId || null;

      const matchesSearch =
        !term ||
        [title, categoryName, advertiserName, product?.priceCondition || ""]
          .join(" ")
          .toLowerCase()
          .includes(term);

      let matchesCategory = true;
      if (categoryFilter) {
        matchesCategory = subcategoryFilter
          ? String(categoryId) === String(subcategoryFilter)
          : String(categoryId) === String(categoryFilter) ||
            String(parentId) === String(categoryFilter);
      }

      const matchesUrgency =
        urgencyFilter === "TODOS" ||
        (urgencyFilter === "COM_URGENCIA" && product?.hasUrgency) ||
        (urgencyFilter === "SEM_URGENCIA" && !product?.hasUrgency);

      return matchesSearch && matchesCategory && matchesUrgency;
    });
    // categoryNameOf depende de categoriesTree, incluído abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    products,
    searchTerm,
    categoryFilter,
    subcategoryFilter,
    urgencyFilter,
    categoriesTree,
  ]);

  const selectableIds = filteredProducts.map((product) => product.id);
  const selectedOnPage = selectedIds.filter((id) => selectableIds.includes(id));
  const allSelected =
    selectableIds.length > 0 && selectedOnPage.length === selectableIds.length;

  const activeChips = [
    searchTerm.trim() && {
      key: "search",
      label: `Busca: "${searchTerm.trim()}"`,
      clear: () => setSearchTerm(""),
    },
    categoryFilter && {
      key: "category",
      label: `Categoria: ${
        categoriesTree.find((cat) => String(cat.id) === String(categoryFilter))
          ?.nome || "selecionada"
      }`,
      clear: () => {
        setCategoryFilter("");
        setSubcategoryFilter("");
      },
    },
    subcategoryFilter && {
      key: "subcategory",
      label: "Subcategoria filtrada",
      clear: () => setSubcategoryFilter(""),
    },
    urgencyFilter !== "TODOS" && {
      key: "urgency",
      label:
        urgencyFilter === "COM_URGENCIA" ? "Com alerta" : "Sem alerta",
      clear: () => setUrgencyFilter("TODOS"),
    },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setSubcategoryFilter("");
    setUrgencyFilter("TODOS");
  };

  const refreshAfterStatusChange = () => {
    fetchCounts();
    if (statusFilter) fetchProducts(currentPage);
  };

  const applyProductStatus = async (product, statusAprovacao, motivoRejeicao = null) => {
    setStatusSavingId(product.id);
    setStatusError("");

    try {
      const response = await axiosInstance.put(`/products/${product.id}/status`, {
        statusAprovacao,
        motivoRejeicao,
      });
      const updated = response?.data?.data;

      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id ? { ...item, ...(updated || {}) } : item
        )
      );
      setSelectedIds((prev) => prev.filter((id) => id !== product.id));
      refreshAfterStatusChange();
      return true;
    } catch (error) {
      setStatusError(
        error?.response?.data?.message ||
          "Não foi possível atualizar o status do produto. Tente novamente."
      );
      return false;
    } finally {
      setStatusSavingId(null);
    }
  };

  const applyBulkStatus = async (statusAprovacao, motivoRejeicao = null) => {
    const targets = filteredProducts.filter((product) =>
      selectedIds.includes(product.id)
    );
    if (targets.length === 0) return true;

    setBulkRunning(true);
    setStatusError("");

    let failures = 0;
    for (const product of targets) {
      try {
        const response = await axiosInstance.put(`/products/${product.id}/status`, {
          statusAprovacao,
          motivoRejeicao,
        });
        const updated = response?.data?.data;
        setProducts((prev) =>
          prev.map((item) =>
            item.id === product.id ? { ...item, ...(updated || {}) } : item
          )
        );
      } catch (error) {
        failures += 1;
      }
    }

    setBulkRunning(false);
    setSelectedIds([]);
    refreshAfterStatusChange();

    if (failures > 0) {
      setStatusError(
        `${failures} de ${targets.length} produto(s) não puderam ser atualizados.`
      );
      return false;
    }
    return true;
  };

  const openRejectModal = (product) => {
    setStatusError("");
    setRejectingBulk(false);
    setRejectReason(product?.motivoRejeicao || "");
    setRejectingProduct(product);
  };

  const openBulkRejectModal = () => {
    setStatusError("");
    setRejectingProduct(null);
    setRejectReason("");
    setRejectingBulk(true);
  };

  const closeRejectModal = () => {
    if (statusSavingId || bulkRunning) return;
    setRejectingProduct(null);
    setRejectingBulk(false);
    setRejectReason("");
    setStatusError("");
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      setStatusError("Informe o motivo da reprovação.");
      return;
    }

    const ok = rejectingBulk
      ? await applyBulkStatus("REJEITADO", rejectReason.trim())
      : rejectingProduct
        ? await applyProductStatus(rejectingProduct, "REJEITADO", rejectReason.trim())
        : false;

    if (ok) {
      setRejectingProduct(null);
      setRejectingBulk(false);
      setRejectReason("");
    }
  };

  const handleDeleteProduct = async (productId) => {
    const confirmed = window.confirm(
      "Deseja realmente excluir este produto? Esta ação não pode ser desfeita."
    );
    if (!confirmed) return;

    setActiveProductId(productId);
    try {
      await axiosInstance.delete(`/products/${productId}`);
      setProducts((prev) => prev.filter((product) => product.id !== productId));
      setSelectedIds((prev) => prev.filter((id) => id !== productId));
      fetchCounts();
    } catch (error) {
      window.alert("Não foi possível excluir o produto. Tente novamente.");
    } finally {
      setActiveProductId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadSpreadsheet(
        "/products/export",
        {
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(subcategoryFilter || categoryFilter
            ? { categoryId: subcategoryFilter || categoryFilter }
            : {}),
          ...(searchTerm.trim() ? { title: searchTerm.trim() } : {}),
        },
        "produtos"
      );
    } catch (error) {
      window.alert("Não foi possível exportar os produtos. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const openPreview = (product) => {
    setPreviewIndex(0);
    setPreviewProduct(product);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setSelectedIds([]);
    setCurrentPage(page);
  };

  const changeStatusTab = (status) => {
    setStatusFilter(status);
    setSelectedIds([]);
    setCurrentPage(1);
  };

  if (loading) return <div className="muted">Carregando...</div>;
  if (!isStaff) {
    return (
      <NoData
        title="Acesso restrito"
        description="Esta área é exclusiva da equipe de revisão (ADMIN ou POWER_USER)."
      />
    );
  }

  const tabCountOf = (status) =>
    status
      ? counts[status] || 0
      : counts.PENDENTE + counts.APROVADO + counts.REJEITADO;

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Fila de produtos</h2>
          <p>
            Revise o catálogo da plataforma: aprovar publica na vitrine, reprovar
            devolve o item ao anunciante com um motivo.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              fetchProducts(currentPage);
              fetchCounts();
            }}
            disabled={loadingProducts}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiRefreshCw size={16} />
            Atualizar
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={handleExport}
            disabled={exporting}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiDownload size={16} />
            {exporting ? "Gerando Excel..." : "Exportar Excel"}
          </button>
        </div>
      </div>

      <div className="section-card">
        <div className="plist-toolbar">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.status || "all"}
              type="button"
              className={`plist-tab${statusFilter === tab.status ? " is-active" : ""}${
                tab.status === "PENDENTE" ? " plist-tab--alert" : ""
              }`}
              onClick={() => changeStatusTab(tab.status)}
            >
              {tab.label}
              <span className="plist-tab__count">{tabCountOf(tab.status)}</span>
            </button>
          ))}
        </div>

        <div className="plist-filters">
          <div className="field" style={{ margin: 0 }}>
            <label className="label" htmlFor="plist-search">
              Buscar
            </label>
            <div className="plist-search">
              <FiSearch className="plist-search__icon" size={16} />
              <input
                id="plist-search"
                className="input"
                type="text"
                placeholder="Produto, anunciante, categoria ou condição"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="plist-search__clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Limpar busca"
                >
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label" htmlFor="plist-category">
              Categoria
            </label>
            <select
              id="plist-category"
              className="select"
              value={categoryFilter}
              onChange={(event) => {
                setCategoryFilter(event.target.value);
                setSubcategoryFilter("");
              }}
              disabled={categoriesLoading}
            >
              <option value="">Todas</option>
              {categoriesTree.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nome || cat.name}
                </option>
              ))}
            </select>
          </div>

          {categoryFilter && (
            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="plist-subcategory">
                Subcategoria
              </label>
              <select
                id="plist-subcategory"
                className="select"
                value={subcategoryFilter}
                onChange={(event) => setSubcategoryFilter(event.target.value)}
                disabled={categoriesLoading}
              >
                <option value="">Todas</option>
                {(
                  categoriesTree.find(
                    (cat) => String(cat.id) === String(categoryFilter)
                  )?.subcategories || []
                ).map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.nome || sub.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="field" style={{ margin: 0 }}>
            <label className="label" htmlFor="plist-urgency">
              Urgência
            </label>
            <select
              id="plist-urgency"
              className="select"
              value={urgencyFilter}
              onChange={(event) => setUrgencyFilter(event.target.value)}
            >
              <option value="TODOS">Todas</option>
              <option value="COM_URGENCIA">Com alerta</option>
              <option value="SEM_URGENCIA">Sem alerta</option>
            </select>
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="plist-chips">
            <span>Filtros:</span>
            {activeChips.map((chip) => (
              <span key={chip.key} className="plist-chip">
                {chip.label}
                <button type="button" onClick={chip.clear} aria-label={`Remover ${chip.label}`}>
                  <FiX size={12} />
                </button>
              </span>
            ))}
            <button type="button" className="plist-chips__reset" onClick={clearAllFilters}>
              Limpar tudo
            </button>
          </div>
        )}

        {statusError && !rejectingProduct && !rejectingBulk && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              backgroundColor: "#fee2e2",
              color: "#b91c1c",
              padding: "0.75rem 0.9rem",
              borderRadius: "0.6rem",
              marginBottom: "0.9rem",
              fontSize: "0.88rem",
            }}
          >
            <FiAlertTriangle size={16} />
            {statusError}
          </div>
        )}

        <div className="muted" style={{ marginBottom: "0.75rem", fontSize: "0.84rem" }}>
          {loadingProducts
            ? "Carregando produtos..."
            : `Exibindo ${filteredProducts.length} de ${totalCount} produto(s)${
                statusFilter ? ` com status ${statusText(statusFilter).toLowerCase()}` : ""
              }`}
        </div>

        {loadingProducts ? (
          <div className="plist-scroll">
            <table className="plist-table">
              <tbody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td style={{ width: 44 }}>
                      <div className="plist-skeleton" style={{ width: 16, height: 16 }} />
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <div className="plist-skeleton plist-skeleton--thumb" />
                        <div style={{ flex: 1 }}>
                          <div className="plist-skeleton" style={{ width: "62%" }} />
                          <div
                            className="plist-skeleton"
                            style={{ width: "34%", height: 10, marginTop: 8 }}
                          />
                        </div>
                      </div>
                    </td>
                    <td><div className="plist-skeleton" style={{ width: "70%" }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 90 }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 70 }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 120 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : products.length === 0 ? (
          <NoData
            title={
              statusFilter
                ? `Nenhum produto ${statusText(statusFilter).toLowerCase()}`
                : "Nenhum produto cadastrado"
            }
            description={
              statusFilter === "PENDENTE"
                ? "A fila está zerada — todos os produtos já foram revisados."
                : "Nenhum produto foi encontrado com esse recorte."
            }
          />
        ) : filteredProducts.length === 0 ? (
          <NoData
            title="Nenhum resultado para os filtros"
            description="Ajuste a busca ou limpe os filtros para ver mais produtos."
          />
        ) : (
          <>
            {selectedOnPage.length > 0 && (
              <div className="plist-bulk">
                <span className="plist-bulk__count">
                  {selectedOnPage.length} produto(s) selecionado(s)
                </span>
                <span className="plist-bulk__spacer" />
                <button
                  type="button"
                  className="is-primary"
                  disabled={bulkRunning}
                  onClick={() => applyBulkStatus("APROVADO")}
                >
                  <FiCheckCircle size={15} />
                  {bulkRunning ? "Aplicando..." : "Aprovar selecionados"}
                </button>
                <button type="button" disabled={bulkRunning} onClick={openBulkRejectModal}>
                  <FiXCircle size={15} />
                  Reprovar selecionados
                </button>
                <button type="button" disabled={bulkRunning} onClick={() => setSelectedIds([])}>
                  Limpar seleção
                </button>
              </div>
            )}

            <div className="plist-scroll">
              <table className="plist-table">
                <thead>
                  <tr>
                    <th className="plist-check">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(event) =>
                          setSelectedIds(event.target.checked ? selectableIds : [])
                        }
                        aria-label="Selecionar todos os produtos da página"
                      />
                    </th>
                    <th>Produto</th>
                    <th>Anunciante</th>
                    <th>Status</th>
                    <th>Atualizado</th>
                    <th style={{ textAlign: "right" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const images = getProductImages(product);
                    const approvalStatus = product?.statusAprovacao || "PENDENTE";
                    const advertiser = product?.AdvertiserCompany || product?.Company;
                    const advertiserName =
                      advertiser?.nomeFantasia || advertiser?.razaoSocial || "Sem anunciante";
                    const isVerified = advertiser?.statusVerificacao === "APROVADO";
                    const isSaving = statusSavingId === product.id;
                    const isMenuOpen = openStatusMenuId === product.id;
                    const isSelected = selectedIds.includes(product.id);
                    const categoryName = categoryNameOf(product);
                    const updatedAt = product?.updatedAt || product?.createdAt;

                    return (
                      <tr key={product.id} className={isSelected ? "is-selected" : ""}>
                        <td className="plist-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(event) =>
                              setSelectedIds((prev) =>
                                event.target.checked
                                  ? [...prev, product.id]
                                  : prev.filter((id) => id !== product.id)
                              )
                            }
                            aria-label={`Selecionar ${product?.title || "produto"}`}
                          />
                        </td>

                        <td>
                          <div className="plist-product">
                            {images.length > 0 ? (
                              <button
                                type="button"
                                className="plist-thumb"
                                onClick={() => openPreview(product)}
                                title="Ver fotos do produto"
                              >
                                <img src={images[0]} alt={product?.title || "Produto"} />
                                {images.length > 1 && (
                                  <span className="plist-thumb__count">
                                    +{images.length - 1}
                                  </span>
                                )}
                              </button>
                            ) : (
                              <div
                                className="plist-thumb plist-thumb--empty"
                                title="Produto sem foto"
                              >
                                <FiImage size={18} />
                              </div>
                            )}

                            <div>
                              <div className="plist-product__name">
                                {product?.title || product?.name || "Sem título"}
                              </div>
                              <div className="plist-product__meta">
                                {categoryName && (
                                  <span className="plist-tag">{categoryName}</span>
                                )}
                                {product?.hasUrgency && (
                                  <span className="plist-tag plist-tag--urgent">
                                    <FiAlertTriangle size={11} />
                                    Laranja Alerta
                                  </span>
                                )}
                                {product?.priceCondition && (
                                  <span>{product.priceCondition}</span>
                                )}
                                {images.length === 0 && (
                                  <span style={{ color: "#b45309", fontWeight: 600 }}>
                                    sem foto
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="plist-seller">
                            <span className="plist-avatar">
                              {initialsOf(advertiserName)}
                            </span>
                            <div>
                              <div className="plist-seller__name">{advertiserName}</div>
                              <span
                                className="plist-seller__status"
                                data-verified={isVerified}
                              >
                                {isVerified ? (
                                  <>
                                    <FiCheckCircle size={11} /> Verificado
                                  </>
                                ) : (
                                  <>
                                    <FiClock size={11} /> Não verificado
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="badge" data-tone={statusTone(approvalStatus)}>
                            {statusText(approvalStatus)}
                          </span>
                          {approvalStatus === "REJEITADO" && product?.motivoRejeicao && (
                            <div className="plist-reason" title={product.motivoRejeicao}>
                              {product.motivoRejeicao}
                            </div>
                          )}
                        </td>

                        <td>
                          <div className="plist-date" title={formatDate(updatedAt)}>
                            {relativeTime(updatedAt) || "-"}
                            <span>{formatDate(updatedAt)}</span>
                          </div>
                        </td>

                        <td>
                          <div className="plist-actions">
                            <div
                              className="plist-review"
                              ref={isMenuOpen ? statusMenuRef : null}
                            >
                              <button
                                type="button"
                                className="plist-review__trigger"
                                disabled={isSaving || bulkRunning}
                                aria-haspopup="menu"
                                aria-expanded={isMenuOpen}
                                onClick={() =>
                                  setOpenStatusMenuId((current) =>
                                    current === product.id ? null : product.id
                                  )
                                }
                              >
                                {isSaving ? "Salvando..." : "Revisar"}
                                <FiChevronDown size={14} />
                              </button>

                              {isMenuOpen && (
                                <div role="menu" className="plist-review__menu">
                                  {/* Só as transições possíveis: o status atual fica de fora. */}
                                  {REVIEW_OPTIONS.filter(
                                    (option) => option.status !== approvalStatus
                                  ).map((option) => {
                                    const OptionIcon = option.icon;

                                    return (
                                      <button
                                        key={option.status}
                                        type="button"
                                        role="menuitem"
                                        className="plist-review__item"
                                        style={{ color: option.color }}
                                        onClick={() => {
                                          setOpenStatusMenuId(null);
                                          if (option.status === "REJEITADO") {
                                            openRejectModal(product);
                                          } else {
                                            applyProductStatus(product, option.status);
                                          }
                                        }}
                                      >
                                        <OptionIcon size={15} />
                                        {option.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              className="plist-icon-button"
                              onClick={() => navigate(`/produtos/${product.id}/editar`)}
                              title="Editar produto"
                              aria-label={`Editar ${product?.title || "produto"}`}
                            >
                              <FiEdit2 size={15} />
                            </button>

                            <button
                              type="button"
                              className="plist-icon-button plist-icon-button--danger"
                              onClick={() => handleDeleteProduct(product.id)}
                              disabled={activeProductId === product.id}
                              title="Excluir produto"
                              aria-label={`Excluir ${product?.title || "produto"}`}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <ResponsiveModal
        isOpen={Boolean(rejectingProduct) || rejectingBulk}
        onClose={closeRejectModal}
        title={rejectingBulk ? "Reprovar produtos selecionados" : "Reprovar produto"}
        subtitle="revisão"
        size="md"
        primaryAction={{
          label: statusSavingId || bulkRunning ? "Reprovando..." : "Confirmar reprovação",
          onClick: handleConfirmReject,
          disabled: Boolean(statusSavingId) || bulkRunning,
        }}
        secondaryAction={{
          label: "Cancelar",
          onClick: closeRejectModal,
          disabled: Boolean(statusSavingId) || bulkRunning,
        }}
      >
        {statusError && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              color: "#b91c1c",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              marginBottom: "0.75rem",
              fontSize: "0.88rem",
            }}
          >
            {statusError}
          </div>
        )}
        <p style={{ marginTop: 0, color: "var(--ink-500)", fontSize: "0.92rem" }}>
          {rejectingBulk ? (
            <>
              O mesmo motivo será aplicado aos{" "}
              <strong>{selectedOnPage.length} produtos selecionados</strong>.
            </>
          ) : (
            <>
              O produto <strong>{rejectingProduct?.title || "-"}</strong> sai da vitrine.
            </>
          )}{" "}
          O motivo abaixo é exibido ao anunciante no painel dele, então seja específico.
        </p>
        <label className="label" htmlFor="plist-reject-reason">
          Motivo da reprovação
        </label>
        <textarea
          id="plist-reject-reason"
          className="textarea"
          rows={4}
          value={rejectReason}
          onChange={(event) => setRejectReason(event.target.value)}
          placeholder="Ex.: fotos com marca d'água de terceiros"
          style={{ width: "100%", resize: "vertical" }}
        />
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={Boolean(previewProduct)}
        onClose={() => setPreviewProduct(null)}
        title={previewProduct?.title || "Fotos do produto"}
        subtitle="catálogo"
        size="lg"
        secondaryAction={{ label: "Fechar", onClick: () => setPreviewProduct(null) }}
      >
        {(() => {
          const images = getProductImages(previewProduct);
          if (images.length === 0) {
            return (
              <p className="muted" style={{ margin: 0 }}>
                Este produto não tem fotos enviadas.
              </p>
            );
          }

          const safeIndex = Math.min(previewIndex, images.length - 1);

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <div
                style={{
                  borderRadius: "0.75rem",
                  border: "1px solid var(--border)",
                  background: "#1a120b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={images[safeIndex]}
                  alt={`${previewProduct?.title || "Produto"} - foto ${safeIndex + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>

              {images.length > 1 && (
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {images.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      onClick={() => setPreviewIndex(index)}
                      style={{
                        padding: 0,
                        border:
                          index === safeIndex
                            ? "2px solid var(--brand-500)"
                            : "1px solid var(--border)",
                        borderRadius: "0.5rem",
                        overflow: "hidden",
                        background: "none",
                        cursor: "pointer",
                        lineHeight: 0,
                      }}
                    >
                      <img
                        src={image}
                        alt={`Miniatura ${index + 1}`}
                        style={{ width: 62, height: 62, objectFit: "cover", display: "block" }}
                      />
                    </button>
                  ))}
                </div>
              )}

              <a
                href={images[safeIndex]}
                target="_blank"
                rel="noreferrer"
                className="muted"
                style={{ fontSize: "0.82rem", textDecoration: "underline" }}
              >
                Abrir imagem em nova aba
              </a>
            </div>
          );
        })()}
      </ResponsiveModal>
    </div>
  );
};

export default AdminProductsPage;
